import { BILLING_ERROR_CODES } from '@aysar/contracts';

import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** A field on the offer-code form that a refusal can be pinned to. */
export type OfferCodeField = 'discountRuleId' | 'productKey' | 'store';

/** What the offer-code surface should say about a refusal, and what to offer next. */
export interface OfferCodeErrorCopy {
  /** A `subscriptions:offerCodes.error.*` key. */
  i18nKey: string;
  /** A colleague wrote first — the answer is a **re-read**, never a retry. */
  isConflict: boolean;
  /** The mapping is not there any more (404). There is nothing to go and read,
   *  so the dialog closes. */
  isGone: boolean;
  /** The controls this refusal belongs on, when the api could not name them
   *  itself. A refusal the api cannot place is placed by the client (P21b). */
  fields: readonly OfferCodeField[];
}

/**
 * The refusals **this** surface can meet, and the key that answers each.
 *
 * ## A third sibling, deliberately not an extension
 *
 * `payment-error.util` is scoped to the order desk by its own docblock and
 * `billing-error.util` to the entitlement viewer by its. One util answering for
 * three surfaces is a map whose entries are unreachable from whichever caller is
 * reading it (P23b3b) — so this is its own file, scoped here, and the argument
 * runs the same way for the next surface.
 *
 * ## The conflict is placed on the KEY, not on the code
 *
 * `billing_offer_code_mapping_conflict` carries `details.offerCode`, which reads
 * as though the code were the duplicate. It is not: the unique index is
 * (rule, product, store), and editing the code changes nothing. So the client
 * re-places it onto the three fields that actually form the key — the operator's
 * next action is to find the existing mapping, or to point this one somewhere
 * else.
 *
 * `null` for anything unmapped, so the caller shows the api's own sentence
 * rather than a generic key this build happened to have.
 */
const KEY_BY_CODE: Readonly<Record<string, string>> = {
  [BILLING_ERROR_CODES.offerCodeMappingNotFound]: 'offerCodes.error.notFound',
  [BILLING_ERROR_CODES.offerCodeMappingConflict]: 'offerCodes.error.conflict',
  [BILLING_ERROR_CODES.discountRuleNotFound]: 'offerCodes.error.ruleGone',
};

const IDENTITY_FIELDS: readonly OfferCodeField[] = ['discountRuleId', 'productKey', 'store'];
const RULE_FIELD: readonly OfferCodeField[] = ['discountRuleId'];
const NO_FIELDS: readonly OfferCodeField[] = [];

export function offerCodeErrorCopy(error: ApiError | null | undefined): OfferCodeErrorCopy | null {
  if (error === null || error === undefined) {
    return null;
  }

  const mapped = KEY_BY_CODE[error.code];

  if (mapped === undefined) {
    return null;
  }

  const isConflict = error.code === BILLING_ERROR_CODES.offerCodeMappingConflict;
  const isRuleGone = error.code === BILLING_ERROR_CODES.discountRuleNotFound;

  return {
    i18nKey: mapped,
    isConflict,
    // Only the mapping's own 404 closes the dialog. A withdrawn RULE is a 422:
    // the mapping being edited is fine, and the recovery is to pick another
    // rule — which the operator cannot do in a dialog that has just closed.
    isGone: error.code === BILLING_ERROR_CODES.offerCodeMappingNotFound,
    fields: isConflict ? IDENTITY_FIELDS : isRuleGone ? RULE_FIELD : NO_FIELDS,
  };
}
